import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Slider,
  FormControlLabel,
  Switch,
  Divider,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  PlaylistPlay,
  Shuffle,
  Favorite,
  History,
  TrendingUp,
  EmojiEmotions,
  LocalFireDepartment,
  Star,
  PlayArrow,
  Settings,
  FilterList,
  Close,
  Psychology,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';

const PlaylistCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #1A1A1A 0%, #2A2A2A 100%)'
    : 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`,
  borderRadius: 12,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 20px rgba(0, 0, 0, 0.3)'
    : '0 4px 20px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 32px rgba(0, 0, 0, 0.5)'
      : '0 12px 32px rgba(0, 0, 0, 0.12)',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#FF6B6B' : '#FF5252'}`,
  },
}));

const MoodButton = styled(Button)(({ theme, mood }) => ({
  borderRadius: 8,
  padding: '12px 20px',
  textTransform: 'none',
  fontWeight: 600,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #2A2A2A 0%, #1A1A1A 100%)'
    : 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
  border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`,
  color: theme.palette.text.primary,
  '&:hover': {
    transform: 'translateY(-2px)',
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #FF6B6B 0%, #FF5252 100%)'
      : 'linear-gradient(135deg, #FF5252 0%, #E53935 100%)',
    color: '#FFFFFF',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 20px rgba(255, 107, 107, 0.3)'
      : '0 8px 20px rgba(255, 82, 82, 0.3)',
  },
}));

const SmartPlaylist = ({ 
  allVideoIds, 
  onPlayVideo, 
  currentVideoId, 
  userPreferences,
  onUpdatePreferences 
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [smartPlaylists, setSmartPlaylists] = useState([]);
  const [selectedMood, setSelectedMood] = useState(null);
  const [playlistPreview, setPlaylistPreview] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);

  // Mood categories with associated keywords and video analysis
  const moods = [
    {
      id: 'feel-good',
      name: 'Feel Good',
      icon: <EmojiEmotions />,
      color: '#FFD700',
      keywords: ['surprise', 'gift', 'happy', 'birthday', 'celebration', 'amazing'],
      description: 'Uplifting and positive vlogs'
    },
    {
      id: 'pranks',
      name: 'Pranks & Chaos',
      icon: <LocalFireDepartment />,
      color: '#FF4500',
      keywords: ['prank', 'chaos', 'crazy', 'insane', 'freakout', 'breaking'],
      description: 'Wild pranks and chaotic moments'
    },
    {
      id: 'emotional',
      name: 'Emotional',
      icon: <Favorite />,
      color: '#FF1493',
      keywords: ['crying', 'emotional', 'reaction', 'heartwarming', 'touching'],
      description: 'Heartwarming and emotional content'
    },
    {
      id: 'trending',
      name: 'Most Popular',
      icon: <TrendingUp />,
      color: '#00FF50',
      keywords: ['million', 'viral', 'best', 'ultimate', 'epic'],
      description: 'Trending and most-watched vlogs'
    },
    {
      id: 'recent',
      name: 'Latest Drops',
      icon: <Star />,
      color: '#9C27B0',
      keywords: [],
      description: 'Newest David Dobrik content'
    }
  ];

  // Generate smart playlist based on mood and user preferences
  const generateSmartPlaylist = useCallback(async (mood, customPreferences = {}) => {
    setIsGenerating(true);
    
    try {
      // Import video metadata functions
      const { getAllVideoData, getVideoMetadata } = await import('../data/videoData');
      const allVideos = await getAllVideoData();
      
      let filteredVideos = [...allVideos];
      
      // Apply mood-based filtering
      if (mood && mood.keywords.length > 0) {
        filteredVideos = filteredVideos.filter(video => 
          mood.keywords.some(keyword => 
            video.title.toLowerCase().includes(keyword.toLowerCase())
          )
        );
      }
      
      // Apply era preferences
      if (userPreferences.preferredEras && userPreferences.preferredEras.length > 0) {
        filteredVideos = filteredVideos.filter(video => 
          userPreferences.preferredEras.includes(video.era)
        );
      }
      
      // Apply duration preferences
      if (userPreferences.preferredDuration) {
        filteredVideos = filteredVideos.filter(video => {
          if (!video.duration) return true;
          
          const duration = parseDuration(video.duration);
          const minutes = parseInt(duration.split(':')[0]);
          
          switch (userPreferences.preferredDuration) {
            case 'short': return minutes <= 5;
            case 'medium': return minutes > 5 && minutes <= 15;
            case 'long': return minutes > 15;
            default: return true;
          }
        });
      }
      
      // Sort by preference
      switch (mood?.id || 'recent') {
        case 'recent':
          filteredVideos.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
          break;
        case 'trending':
          // Simulate popularity based on title keywords and recency
          filteredVideos.sort((a, b) => {
            const aScore = (a.title.toLowerCase().includes('million') ? 10 : 0) +
                          (new Date(a.publishedAt).getFullYear() >= 2020 ? 5 : 0);
            const bScore = (b.title.toLowerCase().includes('million') ? 10 : 0) +
                          (new Date(b.publishedAt).getFullYear() >= 2020 ? 5 : 0);
            return bScore - aScore;
          });
          break;
        default:
          // Shuffle for other moods
          filteredVideos = filteredVideos.sort(() => Math.random() - 0.5);
      }
      
      // Limit to reasonable playlist size
      const playlist = filteredVideos.slice(0, 20).map(video => ({
        id: video.id,
        title: video.title,
        era: video.era,
        duration: video.duration,
        publishedAt: video.publishedAt
      }));
      
      setPlaylistPreview(playlist);
      return playlist;
      
    } catch (error) {
      console.error('Error generating playlist:', error);
      return [];
    } finally {
      setIsGenerating(false);
    }
  }, [userPreferences]);

  // Helper function to parse duration
  const parseDuration = (duration) => {
    if (!duration) return 'Unknown';
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (!match) return 'Unknown';
    const hours = parseInt(match[1] || 0);
    const minutes = parseInt(match[2] || 0);
    const seconds = parseInt(match[3] || 0);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  // Load smart playlists on component mount
  useEffect(() => {
    const loadPlaylists = async () => {
      const generatedPlaylists = await Promise.all(
        moods.slice(0, 3).map(async (mood) => {
          const playlist = await generateSmartPlaylist(mood);
          return {
            id: mood.id,
            name: mood.name,
            icon: mood.icon,
            color: mood.color,
            description: mood.description,
            videos: playlist.slice(0, 10), // Limit to 10 videos per playlist
            count: playlist.length
          };
        })
      );
      setSmartPlaylists(generatedPlaylists);
    };

    if (allVideoIds.length > 0) {
      loadPlaylists();
    }
  }, [allVideoIds, generateSmartPlaylist]);

  const handlePlayPlaylist = (playlist) => {
    if (playlist.videos.length > 0) {
      onPlayVideo(playlist.videos[0].id);
      setOpen(false);
    }
  };

  const handleMoodSelect = (mood) => {
    setSelectedMood(mood);
    generateSmartPlaylist(mood);
  };

  return (
    <>
      {/* Smart Playlist Trigger Button */}
      <Tooltip title="Open Smart Playlists" arrow>
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            bgcolor: 'rgba(156,39,176,0.2)',
            border: '2px solid rgba(156,39,176,0.5)',
            borderRadius: 2,
            color: theme?.palette?.text?.primary,
            transition: 'all 0.3s ease',
            '&:hover': {
              bgcolor: 'rgba(156,39,176,0.4)',
              transform: 'scale(1.1)',
              borderColor: '#9C27B0',
            }
          }}
        >
          <Badge badgeContent={smartPlaylists.length} color="secondary">
            <Psychology />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Smart Playlist Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: (theme) => ({
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 5,
            color: theme.palette.text.primary,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 24px 64px rgba(0, 0, 0, 0.8)'
              : '0 24px 64px rgba(0, 0, 0, 0.15)',
          })
        }}
      >
        <DialogTitle sx={(theme) => ({ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #2A2A2A 0%, #1A1A1A 100%)'
            : 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
          color: theme.palette.text.primary,
          fontWeight: 600,
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 3
        })}>
          <Box display="flex" alignItems="center" gap={2}>
            <Psychology sx={(theme) => ({ 
              color: theme.palette.mode === 'dark' ? '#C0C0C0' : '#9E9E9E' 
            })} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Smart Playlists
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setOpen(false)} 
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.1)'
                  : 'rgba(0, 0, 0, 0.04)',
              }
            })}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={(e, v) => setActiveTab(v)}
            sx={(theme) => ({ 
              mb: 3,
              '& .MuiTab-root': { 
                color: theme.palette.text.secondary,
                fontWeight: 600,
                textTransform: 'none',
              },
              '& .Mui-selected': { 
                color: theme.palette.text.primary,
              },
              '& .MuiTabs-indicator': {
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #FF6B6B 0%, #FF5252 100%)'
                  : 'linear-gradient(135deg, #FF5252 0%, #E53935 100%)',
                height: 3,
                borderRadius: 2,
              },
            })}
          >
            <Tab label="Auto-Generated" />
            <Tab label="Mood Playlists" />
            <Tab label="Preferences" />
          </Tabs>

          {/* Auto-Generated Playlists Tab */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={(theme) => ({ 
                color: theme.palette.text.primary,
                fontWeight: 700 
              })}>
                
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                {smartPlaylists.map((playlist) => (
                  <PlaylistCard key={playlist.id} onClick={() => handlePlayPlaylist(playlist)}>
                    <CardContent>
                      <Box display="flex" alignItems="center" justifyContent="between">
                        <Box display="flex" alignItems="center" gap={2}>
                          <Box sx={{ color: playlist.color, fontSize: 32 }}>
                            {playlist.icon}
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={(theme) => ({
                              color: theme.palette.text.primary,
                              fontWeight: 600
                            })}>
                              {playlist.name}
                            </Typography>
                            <Typography variant="body2" sx={(theme) => ({
                              color: theme.palette.text.secondary
                            })}>
                              {playlist.description} • {playlist.count} videos
                            </Typography>
                          </Box>
                        </Box>
                        <PlayArrow sx={(theme) => ({ 
                          color: theme.palette.text.primary, 
                          fontSize: 32 
                        })} />
                      </Box>
                    </CardContent>
                  </PlaylistCard>
                ))}
              </Box>
            </Box>
          )}

          {/* Mood Playlists Tab */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={(theme) => ({ color: theme.palette.text.primary })} fontWeight={700}>
                
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
                {moods.map((mood) => (
                  <MoodButton
                    key={mood.id}
                    mood={mood}
                    variant={selectedMood?.id === mood.id ? "contained" : "outlined"}
                    startIcon={mood.icon}
                    onClick={() => handleMoodSelect(mood)}
                    sx={{
                      borderColor: mood.color,
                      color: selectedMood?.id === mood.id 
                        ? (theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF')
                        : mood.color,
                      bgcolor: selectedMood?.id === mood.id ? mood.color : 'transparent',
                    }}
                  >
                    {mood.name}
                  </MoodButton>
                ))}
              </Box>
              
              {/* Playlist Preview */}
              {playlistPreview.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={(theme) => ({ color: theme.palette.text.primary })} gutterBottom fontWeight={600}>
                    Preview: {selectedMood?.name} Playlist ({playlistPreview.length} videos)
                  </Typography>
                  <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {playlistPreview.slice(0, 5).map((video, index) => (
                      <ListItem 
                        key={video.id}
                        button
                        onClick={() => onPlayVideo(video.id)}
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.05)',
                          mb: 1,
                          borderRadius: 2,
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                        }}
                      >
                        <ListItemIcon>
                          <Typography sx={(theme) => ({ color: theme.palette.text.primary })} fontWeight={600}>
                            {index + 1}
                          </Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography sx={(theme) => ({ color: theme.palette.text.primary })} fontWeight={500}>
                              {video.title}
                            </Typography>
                          }
                          secondary={
                            <Typography sx={(theme) => ({ 
                              color: theme.palette.text.secondary 
                            })} variant="caption">
                              {video.era} • {parseDuration(video.duration)}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                    {playlistPreview.length > 5 && (
                      <Typography sx={(theme) => ({ 
                        color: theme.palette.text.secondary,
                        textAlign: 'center',
                        mt: 1 
                      })}>
                        ...and {playlistPreview.length - 5} more videos
                      </Typography>
                    )}
                  </List>
                </Box>
              )}
            </Box>
          )}

          {/* Preferences Tab */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom sx={(theme) => ({ color: theme.palette.text.primary })} fontWeight={700}>
                
              </Typography>
              <Box display="flex" flexDirection="column" gap={3}>
                
                {/* Era Preferences */}
                <Box>
                  <Typography variant="subtitle1" sx={(theme) => ({ color: theme.palette.text.primary })} gutterBottom fontWeight={600}>
                    Preferred Eras
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {['2015–2016 Early Squad', '2017–2018 Classic', '2019–2020 Peak', '2021–2022 Post-peak', '2023–Now Comeback'].map((era) => (
                      <Chip
                        key={era}
                        label={era}
                        variant={userPreferences.preferredEras?.includes(era) ? "filled" : "outlined"}
                        onClick={() => {
                          const newEras = userPreferences.preferredEras?.includes(era)
                            ? userPreferences.preferredEras.filter(e => e !== era)
                            : [...(userPreferences.preferredEras || []), era];
                          onUpdatePreferences({ ...userPreferences, preferredEras: newEras });
                        }}
                        sx={(theme) => ({ 
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.5)' 
                            : 'rgba(0,0,0,0.3)',
                          '&.MuiChip-filled': { 
                            bgcolor: '#FF0050',
                            color: '#FFFFFF'
                          }
                        })}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Duration Preferences */}
                <Box>
                  <Typography variant="subtitle1" sx={(theme) => ({ color: theme.palette.text.primary })} gutterBottom fontWeight={600}>
                    Preferred Video Length
                  </Typography>
                  <Box display="flex" gap={1}>
                    {[
                      { id: 'short', label: 'Short (≤5 min)' },
                      { id: 'medium', label: 'Medium (5-15 min)' },
                      { id: 'long', label: 'Long (>15 min)' }
                    ].map((duration) => (
                      <Chip
                        key={duration.id}
                        label={duration.label}
                        variant={userPreferences.preferredDuration === duration.id ? "filled" : "outlined"}
                        onClick={() => onUpdatePreferences({ 
                          ...userPreferences, 
                          preferredDuration: userPreferences.preferredDuration === duration.id ? null : duration.id 
                        })}
                        sx={(theme) => ({ 
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.5)' 
                            : 'rgba(0,0,0,0.3)',
                          '&.MuiChip-filled': { 
                            bgcolor: '#FFD700', 
                            color: '#000000' 
                          }
                        })}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Smart Features */}
                <Box>
                  <Typography variant="subtitle1" sx={(theme) => ({ color: theme.palette.text.primary })} gutterBottom fontWeight={600}>
                    Smart Features
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={userPreferences.avoidRepeats || false}
                        onChange={(e) => onUpdatePreferences({ 
                          ...userPreferences, 
                          avoidRepeats: e.target.checked 
                        })}
                        sx={{ '& .MuiSwitch-thumb': { bgcolor: '#FF0050' } }}
                      />
                    }
                    label="Avoid Recently Watched Videos"
                    sx={(theme) => ({ color: theme.palette.text.primary })}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          {selectedMood && playlistPreview.length > 0 && (
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={() => handlePlayPlaylist({ videos: playlistPreview })}
              sx={{
                bgcolor: selectedMood.color,
                color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                fontWeight: 600,
                '&:hover': { bgcolor: selectedMood.color, opacity: 0.8 }
              }}
            >
              Play {selectedMood.name} Playlist
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SmartPlaylist;